import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Unplug, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { logUpdate } from '@/lib/audit-log';
import { getCreatorTikTok, disconnectCreatorTikTok } from '@/lib/workerApi';

/* ============================================================================
 * A creator's TikTok connection on the Users page
 * ============================================================================
 * The list reads profiles.is_tiktok_connected (on creator_onboarding_progress
 * via p.*), the same flag the app and the Coach portal use. The drawer asks the
 * worker for the connection's details, and disconnecting goes through the
 * worker too: the token table is service-role only, and raw tokens never reach
 * the browser — the audit trail records SHA-256 fingerprints of them.
 * ========================================================================== */

/** TikTok's note glyph — lucide ships no brand icons. */
function TikTokGlyph({ className = 'h-3 w-3' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.79a5.7 5.7 0 1 0 4.91 5.64V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48Z" />
    </svg>
  );
}

/** Status pill for the table, with a disconnect action while connected. */
export function TikTokStatusCell({ connected, onDisconnect }: { connected: boolean; onDisconnect: () => void }) {
  return (
    <div className="flex flex-nowrap items-center gap-1">
      <span
        className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium leading-5 ${connected
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-400'
          : 'border-border bg-muted/50 text-muted-foreground'}`}
      >
        <TikTokGlyph className="h-3 w-3 shrink-0" />
        {connected ? 'Connected' : 'Not connected'}
        {connected && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />}
      </span>
      {connected && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label="Disconnect TikTok"
              onClick={(e) => { e.stopPropagation(); onDisconnect(); }}
            >
              <Unplug className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Disconnect TikTok</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

const fmt = (iso: string | null) => (iso ? format(new Date(iso), 'MMM d, yyyy') : '–');

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
}

/** Drawer card: who the creator is connected as, and the disconnect action. */
export function TikTokConnectionCard({ userId, onDisconnect }: { userId: string; onDisconnect: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['creator-tiktok', userId],
    queryFn: () => getCreatorTikTok(userId),
    staleTime: 30_000,
  });
  const c = data?.connection;
  const connected = !!data?.connected;

  return (
    <div className="overflow-hidden rounded-xl border bg-gradient-to-br from-card to-muted/40">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-foreground text-background">
            <TikTokGlyph className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold leading-tight">TikTok Shop</p>
            <p className="text-xs text-muted-foreground">
              {isLoading ? 'Checking…' : connected ? 'Connected' : c ? 'Token on file, marked disconnected' : 'Not connected'}
            </p>
          </div>
        </div>
        {(connected || c) && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onDisconnect}
          >
            <Unplug className="mr-1.5 h-3.5 w-3.5" />
            Disconnect
          </Button>
        )}
      </div>

      {error ? (
        <p className="px-4 py-3 text-xs text-destructive">{(error as Error).message}</p>
      ) : c ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
          <Detail label="Seller">{c.seller_name || '–'}</Detail>
          <Detail label="Region">{c.seller_base_region || '–'}</Detail>
          <Detail label="Connected since">{fmt(c.connected_at)}</Detail>
          <Detail label="Token refreshed">{fmt(c.updated_at)}</Detail>
          <Detail label="Refresh expires">{fmt(c.refresh_token_expires_at)}</Detail>
          <Detail label="Scopes · shops">{c.granted_scopes.length} · {c.shop_count}</Detail>
          <div className="col-span-2 flex items-center gap-1.5 rounded-md bg-muted/60 px-2.5 py-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
            Token fingerprint <span className="font-mono text-foreground">{c.access_token_fingerprint ?? '–'}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Confirm + run the disconnect. Records the previous connection (fingerprints,
 * never raw tokens) in the admin audit trail on success, and the attempt on
 * failure.
 */
export function DisconnectTikTokDialog({
  creator,
  onClose,
  onDisconnected,
}: {
  creator: { id: string; full_name?: string | null; email?: string | null } | null;
  onClose: () => void;
  onDisconnected: (userId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const name = creator?.full_name || creator?.email || 'this creator';

  const close = () => { if (!busy) { setReason(''); onClose(); } };

  const run = async () => {
    if (!creator) return;
    setBusy(true);
    const meta = { creator_name: creator.full_name ?? null, creator_email: creator.email ?? null, reason: reason.trim() || null };
    try {
      const { previous } = await disconnectCreatorTikTok(creator.id);
      await logUpdate(
        'creator_tiktok_tokens',
        creator.id,
        { is_tiktok_connected: true, connection: previous },
        { is_tiktok_connected: false, connection: null },
        { ...meta, description: `Admin disconnected TikTok for ${name}`, status: 'success' },
      );
      toast.success('TikTok disconnected', { description: `${name} can reconnect from the app or the portal.` });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['creator-tiktok', creator.id] });
      onDisconnected(creator.id);
      setReason('');
      onClose();
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to disconnect';
      await logUpdate('creator_tiktok_tokens', creator.id, { is_tiktok_connected: true }, { is_tiktok_connected: false }, {
        ...meta, description: `Failed to disconnect TikTok for ${name}`, status: 'failed', error: msg,
      });
      toast.error('Could not disconnect TikTok', { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={!!creator} onOpenChange={(open) => { if (!open) close(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <Unplug className="h-4 w-4" />
            </span>
            Disconnect TikTok?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This removes <strong>{name}</strong>’s TikTok Shop connection. Showcase, sales data and Coach
            insights stop until they reconnect from the app or the Coach portal. 
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          placeholder="Reason (optional, saved to the admin log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={500}
          disabled={busy}
        />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => { e.preventDefault(); run(); }}
          >
            {busy ? 'Disconnecting…' : 'Disconnect'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
